import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "472",
  "slug": "what-is-a-geyser-how-geysers-form",
  "title": "What Is a Geyser? How Geysers Form and Why They Erupt",
  "excerpt": "Geysers are rare hot springs that erupt boiling water and steam on a schedule of their own. Here is the geology behind the eruption, and where to find the world's most famous ones.",
  "date": "2026-06-18",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1594494276208-b4dc0b8c1c27?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxnZXlzZXIlMjBlcnVwdGlvbnxlbnwxfDB8fHwxNzgxNzcwNDIxfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "A geyser is a hot spring that periodically erupts boiling water and steam. It needs three things at once: a heat source, plenty of groundwater, and a narrow, sealed plumbing system that lets pressure build until the water flashes to steam. Fewer than a thousand exist worldwide, and about half of them are in Yellowstone.",
  "content": [
    {
      "type": "paragraph",
      "text": "A geyser is one of the rarest things a landscape can do. Most hot springs simply sit there, steaming quietly. A geyser holds its breath, builds pressure underground, and then throws a column of boiling water and steam into the air, sometimes dozens of metres high, before settling back to do it all again. There are fewer than a thousand active geysers on Earth, which makes them rarer than almost any other natural wonder of comparable fame."
    },
    {
      "type": "heading",
      "text": "The Three Ingredients Every Geyser Needs"
    },
    {
      "type": "paragraph",
      "text": "Geysers are picky. They only form where three conditions line up at the same time, which is why they cluster in a handful of places rather than appearing everywhere there is hot ground."
    },
    {
      "type": "list",
      "items": [
        "A heat source. Geysers sit above young volcanic rock or shallow magma, which keeps the deep groundwater far hotter than it would otherwise be.",
        "A reliable water supply. Rain and snowmelt have to seep down through the rock to be heated, so geysers favour places with both volcanism and steady precipitation.",
        "A sealed, constricted plumbing system. This is the rare part. The underground channels must be narrow and lined with a mineral called geyserite, which seals the walls so pressure can build instead of leaking away."
      ]
    },
    {
      "type": "paragraph",
      "text": "Take away any one of these and you get something less dramatic: a hot spring, a fumarole that vents only steam, or a bubbling mud pot. Get all three and you get an eruption."
    },
    {
      "type": "heading",
      "text": "What Actually Happens During an Eruption"
    },
    {
      "type": "paragraph",
      "text": "Water trickles down into the geyser's plumbing and collects deep underground, where it is heated by the surrounding rock. Because of the weight of all the water above it, this deep water is under enormous pressure, and pressure raises the boiling point. So the water keeps heating well past 100 degrees Celsius without actually boiling, the same way a pressure cooker works."
    },
    {
      "type": "paragraph",
      "text": "Eventually some of that superheated water reaches its limit and turns to steam. Steam takes up far more space than liquid water, so the first bubbles shove some water out of the top of the vent. That lightens the load above the deep water, which drops the pressure, which lowers the boiling point, which lets even more water flash to steam all at once. The result is a chain reaction: a sudden, violent expansion that blasts the whole water column skyward. Once the system empties and cools, it quietly refills and the cycle begins again."
    },
    {
      "type": "heading",
      "text": "Why Some Geysers Keep a Schedule"
    },
    {
      "type": "paragraph",
      "text": "Old Faithful in Yellowstone is famous for erupting on a fairly predictable interval, currently somewhere between roughly 60 and 110 minutes apart. That regularity comes from a stable plumbing system that takes a consistent amount of time to refill and reheat. Most geysers are far less polite. Their intervals can stretch from minutes to years, and earthquakes, droughts, and even nearby eruptions can reset their timing or shut them down entirely. A geyser is a delicate machine, and small changes underground have outsized effects at the surface."
    },
    {
      "type": "heading",
      "text": "Where to Find the World's Geysers"
    },
    {
      "type": "paragraph",
      "text": "Geysers are concentrated in just a few geothermal regions, all tied to recent volcanism:"
    },
    {
      "type": "list",
      "items": [
        "Yellowstone, United States, holds roughly half of the world's active geysers, including Old Faithful and Steamboat, the tallest active geyser on Earth.",
        "Iceland gives us the very word. The original Geysir and its energetic neighbour Strokkur sit in a geothermal valley fed by the Mid-Atlantic Ridge.",
        "The Valley of Geysers on Russia's Kamchatka Peninsula is the second-largest geyser field in the world.",
        "El Tatio in Chile, high in the Andes, is the largest geyser field in the Southern Hemisphere.",
        "New Zealand's Taupo Volcanic Zone and the geothermal areas around Rotorua round out the major sites."
      ]
    },
    {
      "type": "paragraph",
      "text": "The English word geyser comes from Geysir in Iceland, which in turn comes from an Old Norse verb meaning to gush. It is one of the few English words borrowed directly from Icelandic."
    },
    {
      "type": "heading",
      "text": "Spotting Geothermal Country From Above"
    },
    {
      "type": "paragraph",
      "text": "From a satellite view, geyser basins announce themselves: pale mineral crusts, steam plumes, bright turquoise and orange hot-spring pools stained by heat-loving microbes, and a notable lack of vegetation on the scalded ground. Learning to read those signals, alongside the volcanic landscapes that create them, is exactly the kind of pattern recognition that makes the planet feel legible. If you want to practise spotting volcanic and geothermal terrain from orbit, EarthGuessr drops you into satellite imagery from around the world and asks one simple question: where on Earth are you?"
    }
  ]
};
